import permissions from '.'
import { useUser } from '@hooks'
import {
  PermissionsType,
  IEmployeeLike,
  IPermissionsOnEmployee,
  IPermissionsOnEmployeeList,
  IPermissionsOnBuilding,
  IPermissionsOnFloor,
  IPermissionsOnAsset,
  IPermissionsOnNeighborhood,
} from './types'
import {
  BMTableBuilding,
  BMDialogBuilding,
  BMDialogFloor,
  IAssetTag,
  INeighborhoodTableRecord,
  INeighborhoodDetails,
} from '@api'

type EmployeeTypeProps = {
  type: PermissionsType.EMPLOYEE
  on: IEmployeeLike
}

type EmployeeListTypeProps = {
  type: PermissionsType.EMPLOYEE_LIST
  on: IEmployeeLike[]
}

type BuildingTypeProps = {
  type: PermissionsType.BUILDING
  on?: BMTableBuilding | BMDialogBuilding
}

type FloorTypeProps = {
  type: PermissionsType.FLOOR
  on?: { id: string }
}

type AssetTypeProps = {
  type: PermissionsType.ASSET
  on?: IAssetTag
}

type NeighborhoodTypeProps = {
  type: PermissionsType.NEIGHBORHOOD
  on?: INeighborhoodTableRecord[] | INeighborhoodDetails[] | null
}

type PermissionsProps =
  | EmployeeTypeProps
  | EmployeeListTypeProps
  | BuildingTypeProps
  | FloorTypeProps
  | AssetTypeProps
  | NeighborhoodTypeProps

export type PermissionTypes =
  | IPermissionsOnEmployee
  | IPermissionsOnEmployeeList
  | IPermissionsOnBuilding
  | IPermissionsOnFloor
  | IPermissionsOnAsset
  | IPermissionsOnNeighborhood

// Usage:
// <Permissions type={Type.EMPLOYEE} on={empl}>
//  {permissions => permissions.canDelete && <Button onClick={...}>Delete</Button>}
// </Permissions>
export default function Permissions({
  children,
  type,
  on: obj,
}: PermissionsProps & {
  children: (permissions: PermissionTypes) => JSX.Element | null
}) {
  const { user } = useUser()

  if (user) {
    switch (type) {
      case PermissionsType.EMPLOYEE:
        return children(permissions.onEmployee(user, obj as IEmployeeLike))
      case PermissionsType.EMPLOYEE_LIST:
        return children(permissions.onEmployeeList(user, obj as IEmployeeLike[]))
      case PermissionsType.BUILDING:
        return children(permissions.onBuilding(user, obj as BMTableBuilding | null))
      case PermissionsType.FLOOR:
        return children(permissions.onFloor(user, obj as BMDialogFloor))
      case PermissionsType.ASSET:
        return children(permissions.onAsset(user, obj as IAssetTag))
      case PermissionsType.NEIGHBORHOOD:
        return children(permissions.onNeighborhood(user, obj as INeighborhoodTableRecord[]))
    }
  }

  return null
}
